import { useRef } from 'react'
import { useQuery } from '@apollo/client'
import { LessonAvailabilityQuery } from 'pages/schedule/queries'
const { DateTime } = require('luxon')

export const aggregateByDate = (timeSlots, tz) => {
  return timeSlots.reduce((acc, ts) => {
    const date = DateTime.fromISO(ts, { zone: tz }).toISODate()
    if (!(date in acc)) {
      acc[date] = []
    }
    acc[date].push(ts)
    return acc
  }, {})
}

export const aggregateByPeriod = (timeSlots, tz) => {
  return timeSlots.reduce(
    (acc, ts) => {
      const period = DateTime.fromISO(ts, { zone: tz }).toFormat('a')
      acc[period].push(ts)
      return acc
    },
    { AM: [], PM: [] },
  )
}

export const AVAILABILITY_LOOKAHEAD_MONTHS = 1

export const availabilityMaxEndDateTime = (currentTime = DateTime.local()) =>
  currentTime.plus({ months: AVAILABILITY_LOOKAHEAD_MONTHS }).endOf('month')

export const parseSelectedDayUtc = selectedDay => {
  if (!selectedDay) return null
  const parsed = DateTime.fromISO(selectedDay, { zone: 'utc' })
  if (!parsed.isValid) return null
  return parsed.startOf('day')
}

export const selectedDayQueryEndBound = selectedDay => {
  const parsedUtc = parseSelectedDayUtc(selectedDay)
  if (!parsedUtc) return null
  return parsedUtc
    .endOf('month')
    .plus({ days: 1 })
    .startOf('day')
}

export const isSelectedDayWithinAvailabilityCap = (
  selectedDay,
  currentTime = DateTime.local(),
) => {
  const parsedUtc = parseSelectedDayUtc(selectedDay)
  if (!parsedUtc) return false
  const maxEndDate = availabilityMaxEndDateTime(currentTime).toISODate()
  return parsedUtc.toISODate() <= maxEndDate
}

export const dateRangeForMonth = (month, currentTime) => {
  const startDateTime = DateTime.max(currentTime, month)
  const endDateTime = startDateTime.endOf('month')

  return { startDateTime, endDateTime }
}

export const resolveSelectedDateFromQuery = ({
  selectedDay,
  timezone,
  datesWithAvailability,
  currentTime = DateTime.local(),
}) => {
  if (!selectedDay || !timezone) return null

  const parsedUtc = parseSelectedDayUtc(selectedDay)
  if (!parsedUtc) return null

  if (!isSelectedDayWithinAvailabilityCap(selectedDay, currentTime)) return null

  const selectedIsoDate = parsedUtc.toISODate()
  const selectedDate = DateTime.fromISO(selectedIsoDate, {
    zone: timezone,
  }).startOf('day')
  const today = currentTime.setZone(timezone).startOf('day')
  if (selectedDate < today) return null

  if (!datesWithAvailability.includes(selectedIsoDate)) return null

  return selectedDate
}

export const initialAvailabilityDateRange = (
  currentTime = DateTime.local(),
) => {
  const startDateTime = DateTime.max(currentTime, currentTime.startOf('month'))
  const endDateTime = availabilityMaxEndDateTime(currentTime)

  return { startDateTime, endDateTime }
}

export const useMonthlyLessonAvailability = (
  locationId,
  lessonTypeId,
  includeDeleted,
) => {
  const currentTime = useRef(DateTime.local())
  const { startDateTime, endDateTime } = initialAvailabilityDateRange(
    currentTime.current,
  )
  return useQuery(LessonAvailabilityQuery, {
    variables: {
      locationId,
      id: lessonTypeId,
      startDateTime,
      endDateTime,
      includeDeleted: includeDeleted || false,
    },
  })
}
